// Headless-WordPress data source for the /blogs pages -- prototype for
// replacing the static BlogsData.jsx array with a real CMS backend, while
// keeping the exact same React rendering (BlogCardsRow / BlogDetail), so
// the pages look identical regardless of where the content comes from.
//
// Uses the `?rest_route=` query-string form of the REST API rather than
// the pretty `/wp-json/...` path -- this WordPress instance's permalink
// setting is still "Plain" (never changed from the install default), so
// no rewrite rule exists yet for the pretty path. Switching permalinks to
// "Post name" in wp-admin would make `/wp-json/...` work too; either form
// returns identical data.
//
// MUST be https:// -- the production site is served over https, and a
// browser blocks an https page from fetching an http URL as mixed content
// ("Failed to fetch"). blog.savorka.in terminates TLS on the VPS with a
// real Let's Encrypt cert (reverse-proxied to the WordPress backend), so
// https works cleanly.
const WP_API_BASE = "https://blog.savorka.in/?rest_route=/wp/v2";

const FALLBACK_CATEGORY = "Blog";
const FALLBACK_AUTHOR = "Savorka";

function formatDate(isoDate) {
  return new Date(isoDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function estimateReadTime(html) {
  const words = html.replace(/<[^>]+>/g, " ").trim().split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

// WordPress's REST API returns .rendered fields HTML-entity-encoded
// (e.g. "&#038;" for "&") on the assumption they'll be inserted via
// dangerouslySetInnerHTML. title/category/author are rendered as plain
// text in BlogCardsRow/BlogDetail, so they need decoding first or the
// raw entity shows up literally on screen.
function decodeHtmlEntities(str) {
  if (!str) return str;
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
}

// Featured image can be missing from _embed even when the post has one set:
// WordPress refuses to serve an attachment's REST record if that attachment is
// still "attached to" a non-public parent (e.g. it was uploaded into an auto-
// draft), so the embedded wp:featuredmedia is silently dropped. Rather than
// leave the banner/thumbnail blank in that case, fall back to the first inline
// image in the post body -- the file itself always serves (it's the media URL,
// not the gated REST record), so it renders fine.
function firstContentImage(html) {
  const match = html && html.match(/<img\b[^>]*\bsrc=["']([^"']+)["']/i);
  return match ? match[1] : null;
}

// Meta descriptions/OG tags need plain text, not the HTML excerpt.rendered
// WordPress returns -- strips tags, decodes entities, and caps length to a
// sane meta-description size.
function toPlainText(html, maxLength = 160) {
  const text = decodeHtmlEntities(html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
}

// Maps one WP REST API post (with _embed data) into the same shape as an
// entry in BlogsData.jsx, so BlogCardsRow/BlogDetail need no changes.
//
// contentHtml is passed through as WordPress rendered it (rather than being
// reverse-engineered into a fixed {heading, subheading, points, description}
// shape via regex) and rendered directly with dangerouslySetInnerHTML +
// the .wp-content CSS theme in styles/index.css. This means any block the
// WordPress editor supports -- numbered lists, images, tables, quotes, extra
// heading levels -- just works, instead of only the exact H2/H3/UL/P pattern
// the old regex parser could recognize.
function mapWpPostToBlog(post, fallbackImage) {
  const embeddedMedia = post._embedded?.["wp:featuredmedia"]?.[0];
  const embeddedTerms = post._embedded?.["wp:term"]?.[0];
  const embeddedAuthor = post._embedded?.author?.[0];

  return {
    id: post.id,
    slug: post.slug,
    category: decodeHtmlEntities(embeddedTerms?.[0]?.name) || FALLBACK_CATEGORY,
    title: decodeHtmlEntities(post.title.rendered),
    shortDescription: post.excerpt.rendered,
    metaDescription: toPlainText(post.excerpt.rendered),
    image: embeddedMedia?.source_url || firstContentImage(post.content.rendered) || fallbackImage,
    author: decodeHtmlEntities(embeddedAuthor?.name) || FALLBACK_AUTHOR,
    date: formatDate(post.date),
    dateIso: post.date,
    readTime: estimateReadTime(post.content.rendered),
    contentHtml: post.content.rendered,
  };
}

export async function fetchBlogsFromWp(fallbackImage) {
  const res = await fetch(`${WP_API_BASE}/posts&_embed=1&per_page=20`);
  if (!res.ok) throw new Error(`WordPress API error: ${res.status}`);
  const posts = await res.json();
  return posts.map((post) => mapWpPostToBlog(post, fallbackImage));
}

export async function fetchBlogBySlugFromWp(slug, fallbackImage) {
  const res = await fetch(`${WP_API_BASE}/posts&slug=${encodeURIComponent(slug)}&_embed=1`);
  if (!res.ok) throw new Error(`WordPress API error: ${res.status}`);
  const posts = await res.json();
  if (posts.length === 0) return null;
  return mapWpPostToBlog(posts[0], fallbackImage);
}
